import { Component, Input, ChangeDetectionStrategy, forwardRef, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'hc-textarea',
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => HcTextareaComponent),
    multi: true
  }]
})
export class HcTextareaComponent implements ControlValueAccessor {
  @Input() placeholder = '';
  @Input() rows = 6;
  @Input() maxlength: number | string = '';
  @Input() minlength: number | string = '';
  @Input() inputId = '';
  @Input() name = '';
  @Input() hasError: boolean | null | undefined = false;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() required = false;
  @Input() showCharCount = false;

  @ViewChild('textareaEl') textareaEl!: ElementRef<HTMLTextAreaElement>;

  value = '';
  focused = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(value: string): void {
    this.value = value ?? '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  onInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.value = textarea.value;
    this.onChange(this.value);
  }

  onFocus(): void { this.focused = true; }

  onBlur(): void {
    this.focused = false;
    this.onTouched();
  }

  get charCount(): number { return this.value?.length || 0; }

  get containerClass(): string {
    const classes = ['hc-textarea'];
    if (this.focused) classes.push('hc-textarea--focused');
    if (this.hasError) classes.push('hc-textarea--error');
    if (this.disabled) classes.push('hc-textarea--disabled');
    if (this.readonly) classes.push('hc-textarea--readonly');
    return classes.join(' ');
  }
}
